import {
  type ImageContent,
  type ImageContentView,
  isImageContent
} from "@prismicio/types-internal/lib/content"
import type { Image } from "@prismicio/types-internal/lib/customtypes"

import {
  type ImageBlockData,
  MAIN_THUMBNAIL_NAME
} from "../../../core/imageUtils"

export interface Point {
  x: number
  y: number
}

export interface Size {
  width: number
  height: number
}

export interface CropArea {
  x: number
  y: number
  width: number
  height: number
}

export interface CroppedImage {
  zoom: number
  /** x coordinate of the crop rect on the original image */
  x: number
  /** y coordinate of the crop rect on the original image */
  y: number

  /** The width of the crop rect over the original image */
  cropWidth: number
  /** The height of the crop rect over the original image */
  cropHeight: number

  /** Final (user defined via W input) height of the image */
  width: number
  /** Final (user defined via H input) height of the image */
  height: number
}

interface UpdateContentViewArgs {
  contentView: ImageContentView
  croppedImage: CroppedImage
}

function updateContentView(args: UpdateContentViewArgs): ImageContentView {
  const { contentView, croppedImage } = args

  const { width, height, x, y, zoom } = croppedImage

  const url = buildCropUrl({
    origin: contentView.origin,
    croppedImage
  })

  return {
    ...contentView,
    width,
    height,
    url,
    edit: {
      ...contentView.edit,
      zoom: zoom,
      crop: {
        x: x,
        y: y
      }
    }
  }
}

interface UpdateImageArgs<ContentType extends ImageContent | ImageBlockData> {
  field: Image
  content: ContentType
  thumbnail: string
  croppedImage: CroppedImage
}

export function updateImage<ContentType extends ImageContent | ImageBlockData>(
  args: UpdateImageArgs<ContentType>
) {
  const { field, content, thumbnail, croppedImage } = args

  // Updating the existing 'main' contentView.
  if (thumbnail === MAIN_THUMBNAIL_NAME) {
    return updateImageMainThumbnail({ content, croppedImage })
  }

  // The image content might not have thumbnails, for example with an image in a rich text.
  const hasThumbnails = isImageContent(content)

  // Updating an existing thumbnail, if it still exists in the model.
  const model = field?.config?.thumbnails?.find((t) => t.name === thumbnail)
  const contentView = hasThumbnails
    ? content.thumbnails?.[thumbnail]
    : undefined
  if (!model || !contentView) return content

  const updatedContentView = updateContentView({
    contentView,
    croppedImage
  })

  if (updatedContentView.url === contentView.url) return content

  return {
    ...content,
    thumbnails: hasThumbnails
      ? {
          ...content.thumbnails,
          [thumbnail]: updatedContentView
        }
      : undefined
  }
}

interface UpdateImageMainThumbnailArgs<T> {
  content: T
  croppedImage: CroppedImage
}

export function updateImageMainThumbnail<
  T extends ImageContent | ImageBlockData
>(args: UpdateImageMainThumbnailArgs<T>) {
  const { content, croppedImage } = args

  const updatedView = updateContentView({
    contentView: content,
    croppedImage
  })
  if (updatedView.url === content.url) return content

  return {
    ...content,
    ...updatedView
  }
}

interface BuildCropUrlArgs {
  origin: ImageContent["origin"]
  /** Image crop parameters */
  croppedImage: CroppedImage
}

export function buildCropUrl(args: BuildCropUrlArgs) {
  const { origin, croppedImage } = args
  const { x, y, width, height, cropHeight, cropWidth } = croppedImage

  const url = new URL(origin.url)

  const hasResize = origin.height !== height || origin.width !== width

  const hasCrop =
    x !== 0 ||
    y !== 0 ||
    cropHeight !== origin.height ||
    cropWidth !== origin.width

  if (hasCrop) {
    const crop = [x, y, cropWidth, cropHeight].map(Math.round).join(",")

    url.searchParams.set("rect", crop)
    url.searchParams.set("w", width.toString())
    url.searchParams.set("h", height.toString())
  }

  if (hasResize) {
    url.searchParams.set("w", width.toString())
    url.searchParams.set("h", height.toString())
  }

  return url.toString()
}

/**
 * Returns the cropped image object from the content view
 * This is used to initialise the cropper state
 */
export function initCroppedImage(contentView: ImageContentView): CroppedImage {
  const {
    edit: {
      crop: { x, y },
      zoom
    },
    width,
    height,
    origin: { width: originWidth, height: originHeight }
  } = contentView

  let cropWidth
  let cropHeight
  const originAspect = originWidth / originHeight
  const cropAspect = width / height
  if (cropAspect >= originAspect) {
    // crop and origin image would be the same width if zoom was equal to 1
    cropWidth = originWidth / zoom
    cropHeight = cropWidth / cropAspect
  } else {
    // crop and origin image would be the same height if zoom was equal to 1
    cropHeight = originHeight / zoom
    cropWidth = cropHeight * cropAspect
  }

  return {
    x,
    y,
    width,
    height,
    zoom,
    cropWidth,
    cropHeight
  }
}
